import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { withRouter } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { setActiveFooterTab } from "../../store/actions";
import BookingCodeSheet from "./BookingCodeSheet";

/**
 * RN SlipBar — design spec (ui_kits/mobile-app/index.html):
 *   48×48 gold circle FAB, `bottom: 76, right: 16`.
 *   Inner: 28×28 dark circle showing cumulative acca odds toFixed(2).
 *   Badge: 15×15 gold pill `top: -6, right: -6` with selection count.
 *   Empty slip → booking-code sheet; otherwise navigate to bet slip.
 */
function SlipBar(props) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const bets = useSelector((state) => state?.Betslip?.bets ?? []);
  // Hide while side drawer or country-selector sheet is open.
  const showSideBar = useSelector(
    (state) => state?.CommonState?.showSideBar ?? false,
  );
  const showCountrySelector = useSelector(
    (state) => state?.CommonState?.showCountrySelector ?? false,
  );
  const hideSlipBar = showSideBar || showCountrySelector;
  const [sheetOpen, setSheetOpen] = useState(false);

  // Cumulative acca odds — mirrors design: picks.reduce((a,p) => a * p.odds, 1).toFixed(2)
  const accumOdds = bets
    .reduce((a, b) => a * (parseFloat(b.odds) || 1), 1)
    .toFixed(2);

  return (
    <>
      {!hideSlipBar && (
        <button
          type="button"
          className="lg:hidden"
          style={{
            position: "fixed",
            right: 16,
            bottom: 76,
            zIndex: 55,
            width: 48,
            height: 48,
            borderRadius: "50%",
            background: "var(--gold-500,#FFED00)",
            border: 0,
            boxShadow: "0 8px 24px -4px rgba(255, 237, 0,.6)",
            display: "grid",
            placeItems: "center",
            cursor: "pointer",
          }}
          onClick={() => {
            if (bets.length === 0) {
              setSheetOpen(true);
            } else {
              dispatch(setActiveFooterTab("betslip"));
              props.history.push("/betSlip");
            }
          }}
          aria-label={t(
            "betslip_fab_aria_label",
            "Bet slip, {count} selection{suffix}",
            { count: bets.length, suffix: bets.length !== 1 ? "s" : "" },
          )}
        >
          {/* 28×28 dark inner circle — shows cumulative acca odds */}
          <span
            style={{
              position: "relative",
              width: 28,
              height: 28,
              borderRadius: "50%",
              background: "var(--ink-950,#0b131c)",
              color: "var(--text-link,var(--gold-500,#FFED00))",
              display: "grid",
              placeItems: "center",
              font: "var(--type-mono)",
              fontSize: 9,
              fontWeight: 700,
            }}
          >
            {accumOdds}

            {/* 15×15 selection-count badge — top-right of inner circle */}
            {bets.length > 0 && (
              <span
                style={{
                  position: "absolute",
                  top: -6,
                  right: -6,
                  minWidth: 15,
                  height: 15,
                  padding: "0 3px",
                  borderRadius: "var(--radius-pill,9999px)",
                  background: "var(--gold-500,#FFED00)",
                  color: "var(--on-brand,#0b131c)",
                  font: "var(--type-mono)",
                  fontSize: 9,
                  display: "grid",
                  placeItems: "center",
                  lineHeight: 1,
                }}
              >
                {bets.length}
              </span>
            )}
          </span>
        </button>
      )}

      <BookingCodeSheet isOpen={sheetOpen} onClose={() => setSheetOpen(false)} />
    </>
  );
}

export default withRouter(SlipBar);
